import { ReactElement, useEffect } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { CompanyUserRole } from '@sc/graphql';
import { useMutation } from '@apollo/client/react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useUser } from '../providers/users';
import { useAlerts } from '../providers/alerts';
import {
  GQL_FRAGMENT_USER,
  GQL_MUTATION_CONFIRM_EMAIL,
} from '../utility/queries';
import { SettingsAccountTab } from './SettingsAccountTab';
import { SettingsCompanyTab } from './SettingsCompanyTab';
import { SettingsNotificationsTab } from './SettingsNotificationsTab';
import { SettingsUsageLimitsTab } from './SettingsUsageLimitsTab';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';

const tabClassName =
  'pt-3 px-3 pb-2.5 font-semibold data-selected:text-primary-400 hover:bg-grey-t-800 data-selected:border-primary-400 border-transparent border-b-2 outline-none';

export const Settings = (): ReactElement => {
  const { user } = useUser();
  const isAccountOwnerOrAdmin =
    user.role === CompanyUserRole.accountOwner ||
    user.role === CompanyUserRole.admin;

  const { hash } = useLocation();
  const navigate = useNavigate();

  const tabNames = isAccountOwnerOrAdmin
    ? ['Account', 'Company settings', 'Usage limits', 'Notifications']
    : ['Account', 'Notifications'];

  const selectedIndex = hash ? parseInt(hash) : 0;

  const selectedTab = tabNames[selectedIndex];

  useEffect(() => {
    if (selectedTab) track('Settings Tab Viewed', { tab: selectedTab });
  }, [selectedTab]);

  const search = useSearch({
    from: '/_authenticated/settings',
  });

  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const [confirmEmail] = useMutation(GQL_MUTATION_CONFIRM_EMAIL, {
    onError,
    update: (cache) => {
      const id = cache.identify(user);

      if (!id) return;

      cache.updateFragment({ fragment: GQL_FRAGMENT_USER, id }, (existing) =>
        existing
          ? {
            ...existing,
            pendingEmail: null,
            hasPendingEmail: false,
            email: user.pendingEmail ?? existing.email,
            verifiedEmailAt: new Date().toISOString(),
          }
          : null
      );
    },
  });

  useEffect(() => {
    if (!search.verifyEmail) return;

    const [verificationBase64, emailBase64] = decodeURIComponent(
      search.verifyEmail
    ).split('.');

    if (!verificationBase64 || !emailBase64) return;

    void confirmEmail({
      variables: {
        input: {
          verification: atob(verificationBase64),
          email: atob(emailBase64),
        },
      },
      onCompleted: () => {
        notify.success('Successfully updated email.');
      },
    }).finally(
      () =>
        void navigate({
          to: '/settings',
          search: (prev) => ({ ...prev, verifyEmail: undefined }),
          replace: true,
        })
    );
  }, [search.verifyEmail, confirmEmail, navigate]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="p-5">
        <h1 className="text-h1-small font-semibold font-grotesque">Account</h1>
      </div>
      <TabGroup
        selectedIndex={selectedIndex}
        onChange={(index) =>
          void navigate({
            to: '/settings',
            hash: String(index),
          })
        }
        className="flex flex-col grow overflow-hidden"
      >
        <TabList className="space-x-2 px-5 border-b border-grey-t-800">
          <Tab className={tabClassName}>Account</Tab>
          {isAccountOwnerOrAdmin && (
            <>
              <Tab className={tabClassName}>Company settings</Tab>
              <Tab className={tabClassName}>Usage limits</Tab>
            </>
          )}
          <Tab className={tabClassName}>Notifications</Tab>
        </TabList>
        <TabPanels className="grow overflow-y-auto flex flex-col">
          <TabPanel>
            <SettingsAccountTab />
          </TabPanel>
          {isAccountOwnerOrAdmin && (
            <>
              <TabPanel>
                <SettingsCompanyTab />
              </TabPanel>
              <TabPanel>
                <SettingsUsageLimitsTab />
              </TabPanel>
            </>
          )}
          <TabPanel className="grow flex flex-col">
            <SettingsNotificationsTab />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  );
};
